import {
  computed, readonly, ref, useContext, useRoute,
} from '@nuxtjs/composition-api';
import { addItemCommand } from '~/modules/checkout/composables/useCart/commands/addItemCommand';
import { applyCouponCommand } from '~/modules/checkout/composables/useCart/commands/applyCouponCommand';
import { loadCartCommand } from '~/modules/checkout/composables/useCart/commands/loadCartCommand';
import { loadTotalQtyCommand } from '~/modules/checkout/composables/useCart/commands/loadTotalQtyCommand';
import { removeCouponCommand } from '~/modules/checkout/composables/useCart/commands/removeCouponCommand';
import { removeItemCommand } from '~/modules/checkout/composables/useCart/commands/removeItemCommand';
import { updateItemQtyCommand } from '~/modules/checkout/composables/useCart/commands/updateItemQtyCommand';
import { Logger } from '~/helpers/logger';
import {
  Cart, CartItemInterface, ProductInterface, ProductStockStatus,
} from '~/modules/GraphQL/types';
import { useCartStore } from '~/modules/checkout/stores/cart';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { Product, ProductInfoForCombiningInCartQuery } from '~/modules/catalog/product/types';
import { ComposableFunctionArgs } from '~/composables';
import { UseCartErrors, UseCartInterface } from './useCart';
import { useApi } from '~/composables';
import getProductInfoForCombiningInCart from '~/modules/catalog/product/queries/getProductInfoForCombiningInCart.gql';
import GET_CART from './getCart.gql'
import { NuxtCookies } from 'cookie-universal-nuxt';
import { emptyCartCommand } from './commands/emptyCartCommand';
/**
 * Allows loading and manipulating cart of the current user.
 *
 * See the {@link UseCartInterface} for a list of methods and values available in this composable.
 */
export function useCart<CART extends Cart, CART_ITEM extends CartItemInterface, PRODUCT extends ProductInterface>(): UseCartInterface<
  CART,
  CART_ITEM,
  PRODUCT
> {
  const loading = ref<boolean>(false);
  const error = ref<UseCartErrors>({
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    load: null,
    clear: null,
    applyCoupon: null,
    removeCoupon: null,
    loadTotalQty: null,
  });
  const { app, $cookies} = useContext();
  const context = app.$vsf;
  const route = useRoute();
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart as CART & {raw_id});
  const apiState = context.$magento.config.state;
  const { loading: wishlistLoading, afterAddingWishlistItemToCart } = useWishlist();
  const { query } = useApi();
  /**
   * Assign new cart object
   * @param newCart
   *
   * @return void
   */
  const setCart = (newCart: CART): void => {
    Logger.debug('useCart.setCart', newCart);

    cartStore.$patch((state) => {
      state.cart = newCart;
    });
  };

  /**
   * Check if product is in the cart
   * @param product
   *
   * @return boolean
   */
  const isInCart = (product: PRODUCT): boolean => !!cart.value?.items?.find((cartItem) => cartItem?.product?.uid === product.uid);

  const load = async ({ customQuery = {}, customHeaders = {}, realCart = false, newCart = false } = { customQuery: { cart: 'cart' }, customHeaders: {} }): Promise<void> => {
    Logger.debug('useCart.load');

    try {
      loading.value = true;
      // @ts-ignore
      const token = await app.context.$vsf.$magento.api.getCustomerToken();
      let cartId = cartStore.cart?.id;
      const loadedCart = await loadCartCommand.execute(context, { customQuery, customHeaders: { ...customHeaders, ...(token?.data ? {Authorization: 'Bearer '+token.data} : {})}, realCart, newCart });
      const { data } = (cartId || loadedCart?.id) ? await context.$magento.api.customQuery({ query: GET_CART, queryVariables: { cartId: cartId ?? loadedCart?.id} }) as {data:{cart: Cart}} : {data: null}
      cartStore.$patch((state) => {
        state.cart = data?.cart ? data.cart : loadedCart;
        if(state?.cart?.id) $cookies.set('24orepro-vsf-cart',state.cart.id, {domain:'.ilsole24ore.com', path:'/', expires: new Date(new Date().getTime()+(1000*60*60*24*7)) }) //@TODO path / scad 7dd
      });
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('useCart/load', err);
    } finally {
      loading.value = false;
    }
  };

  const clear = async ({ customQuery, customHeaders } = { customQuery: { cart: 'cart' }, customHeaders: {} }): Promise<void> => {
    Logger.debug('useCart.clear');

    try {
      loading.value = true;
//      context.$magento.config.state.removeCartId();
      const loadedCart = await emptyCartCommand.execute(context, { currentCart: cartStore.cart, customHeaders });

      cartStore.$patch((state) => {
        state.cart = loadedCart;
      });
    } catch (err) {
      error.value.clear = err;
      console.error('CCCC', err);
      Logger.error('useCart/clear', err);
    } finally {
      loading.value = false;
    }
  };

  const loadTotalQty = async (params?: ComposableFunctionArgs<{}>): Promise<void> => {
    Logger.debug('useCart.loadTotalQty');

    try {
      loading.value = true;
      const totalQuantity = await loadTotalQtyCommand.execute(context, params);

      cartStore.$patch((state) => {
        state.cart.total_quantity = totalQuantity;
      });
    } catch (err) {
      error.value.loadTotalQty = err;
      Logger.error('useCart/loadTotalQty', err);
    } finally {
      loading.value = false;
    }
  };

  const addItem = async ({
    product, quantity, productConfiguration, customQuery, customHeaders,
  }): Promise<void> => {
    Logger.debug('useCart.addItem', { product, quantity });

    try {
      loading.value = true;

      if (!apiState.getCartId()) {
        await load({ realCart: true });
      }

      const updatedCart = await addItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        quantity,
        productConfiguration,
        customQuery,
        customHeaders,
      });

      error.value.addItem = null;
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    } catch (err) {
      error.value.addItem = err;
      Logger.error('useCart/addItem', err);
    } finally {
      if (!wishlistLoading.value && route.value.query?.wishlist) {
        afterAddingWishlistItemToCart({
          product,
          cartError: error.value.addItem,
        });
      }
      loading.value = false;
    }
  };

  const removeItem = async ({ product, customQuery, customHeaders }) => {
    Logger.debug('useCart.removeItem', { product });

    try {
      loading.value = true;
      const updatedCart = await removeItemCommand.execute(context, {
        currentCart: cart.value,
        product,
        customQuery,
        customHeaders,
      });

      error.value.removeItem = null;
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    } catch (err) {
      error.value.removeItem = err;
      Logger.error('useCart/removeItem', err);
    } finally {
      loading.value = false;
    }
  };

  const updateItemQty = async ({ product, quantity, customQuery = { updateCartItems: 'updateCartItems' } }): Promise<void> => {
    Logger.debug('useCart.updateItemQty', {
      product,
      quantity,
    });

    if (quantity && quantity > 0) {
      try {
        loading.value = true;
        const updatedCart = await updateItemQtyCommand.execute(context, {
          currentCart: cart.value,
          product,
          quantity,
          customQuery,
        });

        error.value.updateItemQty = null;
        cartStore.$patch((state) => {
          state.cart = updatedCart;
        });
      } catch (err) {
        error.value.updateItemQty = err;
        Logger.error('useCart/updateItemQty', err);
      } finally {
        loading.value = false;
      }
    }
  };

  const handleCoupon = async (couponCode = null, customQuery = null): Promise<void> => {
    const variables = {
      currentCart: cart.value,
      customQuery,
      couponCode,
    };

    const { updatedCart, errors } = couponCode
      ? await applyCouponCommand.execute(context, variables)
      : await removeCouponCommand.execute(context, variables);

    if (errors) {
      throw errors[0];
    }

    if (updatedCart) {
      cartStore.$patch((state) => {
        state.cart = updatedCart;
      });
    }
  };

  const applyCoupon = async ({ couponCode, customQuery }): Promise<void> => {
    Logger.debug('useCart.applyCoupon');

    try {
      loading.value = true;
      await handleCoupon(couponCode, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.applyCoupon = err;
      Logger.error('useCart/applyCoupon', err);
    } finally {
      loading.value = false;
    }
  };

  const removeCoupon = async ({ customQuery }): Promise<void> => {
    Logger.debug('useCart.removeCoupon');

    try {
      loading.value = true;
      await handleCoupon(null, customQuery);
      error.value.applyCoupon = null;
    } catch (err) {
      error.value.removeCoupon = err;
      Logger.error('useCart/removeCoupon', err);
    } finally {
      loading.value = false;
    }
  };

  const canAddToCart = (product: Product, qty = 1) => {
    // eslint-disable-next-line no-underscore-dangle
    if (product?.__typename === 'ConfigurableProduct') {
      /* return !!product?.configurable_product_options_selection?.variant
         ?.uid;*/
      /** Just check if there are options selectable. */
      return product?.configurable_product_options_selection?.options_available_for_selection?.length > 0
    }
    const inStock = product?.stock_status === ProductStockStatus.InStock;
    const stockLeft = product?.only_x_left_in_stock === null
      ? true
      : qty <= product?.only_x_left_in_stock;
    return inStock && stockLeft;
  };

  const canCombineProductToCart = async product => {
    let noExportInCart = false;
    const cartItems = cart.value.items;
    if (!cartItems || cartItems.length < 1) return true;

    const skus = cartItems.map(p => p.product.sku);
    let queryInput = { filter: { sku: { in: skus } } };
    const { data } = await query<ProductInfoForCombiningInCartQuery>(
      getProductInfoForCombiningInCart,
      queryInput
    );

    let cartProducts = []
    data.products.items.forEach(product => {
      const cartItem = cart.value.items.find(el => el.product.sku == product.sku);
      if (cartItem['configured_variant'] && cartItem['configured_variant'].sku) {
        cartProducts.push(product.variants.find(v=>v.product.sku==cartItem['configured_variant'].sku).product)
      } else {
        cartProducts.push(product)
      }
    })



    // Cart check.
    const nonExportableProductInCart = cartProducts.some(p => !!p.no_bzt_export)
    const exportableProductInCart = cartProducts.some(p => !p.no_bzt_export)
    const zuoraRestrictedInCart = cartProducts.some(p => !!p.zuora_completed && !p.fulfillment_enabled)
    // Se nel carrello hai un prodotto Zuora NON gestibile tramite accounting. Non è possibile avere altri carrelli.
    if (zuoraRestrictedInCart) { 
      if(product.zuora_completed == true) {
        return true;
      } else {
        return false;
      }
       }
    else if (product.zuora_completed && !product.fulfillment_enabled && cart.value.items.length > 0) { noExportInCart = true; }
    // Sto aggiungendo un prodotto NON a gestione manuale, quando nel carrello c'è già un prodotto a gestione manuale.
    else if (!product.no_bzt_export && nonExportableProductInCart) { noExportInCart = true; }
    // Sto aggiungendo un prodotto A gestione manuale, ma nel carrello ho un prodotto NON a gestione manuale
    else if (product.no_bzt_export && exportableProductInCart) { noExportInCart = true; }
    else if (product.no_bzt_export && nonExportableProductInCart) { noExportInCart = false; }
    return !noExportInCart;
  }

  return {
    setCart,
    cart,
    loadTotalQty,
    isInCart,
    addItem,
    load,
    removeItem,
    clear,
    updateItemQty,
    applyCoupon,
    removeCoupon,
    canAddToCart,
    loading: readonly(loading),
    error: readonly(error),
    canCombineProductToCart
  };
}

export default useCart;
export * from './useCart';
